import { isEmpty } from 'lodash-es';
import { addDays, parseJSON } from 'date-fns';

import { readCookie, writeCookie } from '../utils/cookie';

export interface UserInfo {
    sub: number;
    email: string;
    name: string;
    role: string[];
    has_local_account: Boolean;
    admin_username?: string;
    return_to?: string;
}

const usePrivateState = defineStore('authPrivateStateStore', () => {
    const sessionDuration = ref<Date>();
    const accessToken = ref<string>();
    const refreshToken = ref<string>();
    const userInfo = ref<UserInfo>();

    return { accessToken, refreshToken, userInfo, sessionDuration };
});

export const useUserSessionStore = defineStore('authUserSessionStore', () => {
    const options = useRuntimeConfig().public.auth;
    const app = useNuxtApp();

    // Creating private state
    // https://masteringpinia.com/blog/how-to-create-private-state-in-stores
    const privateState = usePrivateState();

    const accessToken = computed(() => privateState.accessToken);
    const refreshToken = computed(() => privateState.refreshToken);
    const userInfo = computed(() => privateState.userInfo);

    const isAuthenticated = computed(() => accessToken.value != null);
    const userId = computed(() => (isAuthenticated && userInfo.value != null ? userInfo.value.sub : undefined));
    const hasLocalAccount = computed(
        () => isAuthenticated.value && userInfo.value != null && userInfo.value.has_local_account,
    );
    const isLoggedInAsAnotherUser = computed(
        () => isAuthenticated.value && userInfo.value != null && userInfo.value.admin_username != null,
    );
    const isAdmin = computed(() => isAuthenticated.value && isInRole('Admin'));

    function setTokens(newAccessToken: string, newRefreshToken?: string, rememberMe?: boolean) {
        if (rememberMe) privateState.sessionDuration = addDays(new Date(), options.rememberMeDuration);

        privateState.accessToken = newAccessToken;
        privateState.refreshToken = newRefreshToken;

        writeCookies();
    }

    function setUserInfo(val?: UserInfo) {
        const value = !isEmpty(val) ? val : undefined;

        privateState.userInfo = value;

        writeCookies();
    }

    function isInRole(roles: string | string[]) {
        if (!userInfo.value) return false;

        const rolesToMatch = typeof roles === 'string' ? [roles] : roles;

        return userInfo.value.role.some((r) => rolesToMatch.includes(r));
    }

    function clear() {
        privateState.accessToken = undefined;
        privateState.refreshToken = undefined;
        privateState.userInfo = undefined;
        privateState.sessionDuration = undefined;

        writeCookies();
    }

    // Read tokens from cookies on initial page load or page refresh
    function init() {
        const app = useNuxtApp();

        privateState.sessionDuration = readCookie<Date>(app, 'sessionDuration', (val: string) => {
            const date = parseJSON(val);

            // Check validity of date
            return date instanceof Date && !isNaN(date.valueOf()) ? date : undefined;
        });
        privateState.accessToken = readCookie<string>(app, 'accessToken');
        privateState.refreshToken = readCookie<string>(app, 'refreshToken');
        privateState.userInfo = readCookie<UserInfo>(app, 'userInfo');
    }

    function writeCookies() {
        writeCookie(app, 'sessionDuration', privateState.sessionDuration, {
            expires: addDays(new Date(), options.rememberMeDuration),
        });
        writeCookie(app, 'accessToken', privateState.accessToken, { expires: privateState.sessionDuration });
        writeCookie(app, 'refreshToken', privateState.refreshToken, { expires: privateState.sessionDuration });
        writeCookie(app, 'userInfo', privateState.userInfo, { expires: privateState.sessionDuration });
    }

    return {
        accessToken,
        refreshToken,
        userInfo,
        isAuthenticated,
        userId,
        hasLocalAccount,
        isLoggedInAsAnotherUser,
        isAdmin,
        setTokens,
        setUserInfo,
        isInRole,
        clear,
        init,
    };
});
